import React, { useEffect, useMemo, useState, useContext } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { Save, Add } from '@mui/icons-material';
import { isOk } from '../utils/errors';
import { LANGUAGES, LANGUAGE_MAP } from '../utils/lang';
import { useSnackbar } from 'notistack';
import { DataAPIContext } from '../api';
import { UPDATE, GET_ONE, GET_MANY } from '../api/dataProvider';
import AutoTaggingRulesTab from './AutoTaggingRulesTab';
import AIMetricsTab from './AIMetricsTab';
import KnowledgeReviewTab from './knowledgeReview/KnowledgeReviewTab';
import ReminderGapsTab from './reminderGaps/ReminderGapsTab';
import {
  createMockApi as createReminderGapsMockApi, reminderGapsApi, reminderGapsLiveFor, reminderGapsVisibleFor,
} from './reminderGaps/reminderGapsApi';
import { setTrainingInboxCount } from './knowledgeReview/inboxCounts';
import {
  getKnowledgeReviewApi, inboxOperatorFor, isLiveFor, trainingInboxVisibleFor,
} from './knowledgeReview/knowledgeReviewApi';
import { Switch, FormControl, Select, MenuItem, InputLabel, Slider, Divider, Tooltip, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { set as IndexedDB_set } from 'idb-keyval';
import DeleteIconButton from '../components/DeleteIconButton';
import BlockRedirect from '../components/BlockRedirect';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { LEAVE_WITHOUT_SAVING_MESSAGE } from '../components/useLeavePrompt';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import AutoReplyRulesTable from './AutoReplyRulesTable';
import AILearningSourceSetting, { normalizeTeamMember } from './AILearningSourceSetting';
import MissedTourRebookingSetting from './MissedTourRebookingSetting';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import { ContextStore } from '../store/contextStore';
import { a11yProps } from '../components/TabPanel';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { isWebWidgetAvailable } from './webWidgetAvailable';

const TAGS_RESOURCE = 'tour_tag';
const TOURS_RESOURCE = 'tour';
const SYSTEM_TAGS_RESOURCE = 'system_tag';
// -1 = pause indefinitely after a staff reply (bot does not resume).
const PAUSE_AFTER_MANUAL_UNLIMITED = -1;
const PAUSE_AFTER_MANUAL_OPTIONS = [5, 10, 15, 30, 60, 120, 240, PAUSE_AFTER_MANUAL_UNLIMITED];
const pauseAfterManualLabel = (m) => (m === PAUSE_AFTER_MANUAL_UNLIMITED ? 'Unlimited' : `${m} min`);
// Internal/conversation tag category code; participant tags are every other category.
const INTERNAL_TAG_CATEGORY_CODE = 'chat_internal';
// Minimum recommended auto-reply confidence. The 0 → floor range is hatched on the
// confidence slider to mark it as a discouraged zone.
const AUTO_REPLY_CONFIDENCE_FLOOR = 0.8;
// Message sources the AI can be told to skip entirely — no suggestion AND no auto-reply,
// unlike the tour/tag exclusions above which only block the send. Values must match
// ChatMessage.source_platform exactly (backend key: ai_exclude_source_platforms).
const AI_SOURCE_PLATFORM_OPTIONS = [
  { value: 'AIRBNB-GROUP', label: 'Airbnb group thread' },
  { value: 'AIRBNB', label: 'Airbnb (direct thread)' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'WEB_WIDGET', label: 'Web chat widget' },
];
// Mirrors DEFAULT_AI_EXCLUDED_SOURCE_PLATFORMS in demo/models.py: what the backend
// applies when the operator has never set the key. Shown pre-filled so the default is
// visible and removable rather than an invisible rule.
const DEFAULT_AI_EXCLUDED_SOURCE_PLATFORMS = ['AIRBNB-GROUP'];
const sourcePlatformLabel = (value) =>
  AI_SOURCE_PLATFORM_OPTIONS.find((o) => o.value === value)?.label || value;

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

/* ===================== Tag helpers ===================== */

// Convert any incoming shape to a normalized { id, name } object
const normalizeTag = (t) => {
  if (!t) return { id: null, name: '' };
  if (typeof t === 'string') return { id: null, name: t };
  if (typeof t === 'object') {
    const id = t.id ?? t._id ?? t.value ?? t?.data?.id ?? null;
    const name = t.name ?? t.label ?? t?.data?.name ?? '';
    return { id, name };
  }
  return { id: null, name: String(t) };
};

// Extract display name from any shape
const tagToName = (t) => normalizeTag(t).name;

// Extract id from any shape (null if none)
const tagToId = (t) => normalizeTag(t).id;

// Compare two tag-like values (prefer id, else name)
const tagsEqual = (a, b) => {
  const aid = tagToId(a); const bid = tagToId(b);
  if (aid && bid) return aid === bid;
  return tagToName(a).toLowerCase() === tagToName(b).toLowerCase();
};

// Render options with matching substring bolded
const renderBoldTagOption = (props, option, { inputValue }) => {
  const label = tagToName(option);
  if (!inputValue) {
    return (
      <Box component="li" {...props}>
        {label}
      </Box>
    );
  }
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(esc(inputValue), 'ig');
  const html = label.replace(re, (m) => `<b style="color:black !important;">${m}</b>`);
  return (
    <Box component="li" {...props}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};

/* ===================== Tour helpers (auto-reply scope) ===================== */

const normalizeTour = (t) => {
  if (!t) return { id: null, name: '' };
  if (typeof t === 'string') return { id: null, name: t };
  if (typeof t === 'object') {
    const id = t.id ?? t._id ?? t.value ?? t?.data?.id ?? t?.pk ?? null;
    const name = t.name ?? t.title ?? t.label ?? t?.data?.name ?? t?.text ?? '';
    // Keep the bespoke/date fields when present so labels can show them.
    const extra = {};
    for (const k of ['is_bespoke', 'start_date', 'end_date']) {
      if (t[k] !== undefined) extra[k] = t[k];
    }
    return { id, name, ...extra };
  }
  return { id: null, name: String(t) };
};
const tourToId = (t) => normalizeTour(t).id;
const tourToName = (t) => normalizeTour(t).name;
const tourToLabel = (t) => formatTourOptionLabel(normalizeTour(t));
const toursEqual = (a, b) => {
  const A = normalizeTour(a); const B = normalizeTour(b);
  if (A.id && B.id) return String(A.id) === String(B.id);
  return tourToName(A).toLowerCase() === tourToName(B).toLowerCase();
};

export default function TrainingMaterialsTabs() {
  const { enqueueSnackbar } = useSnackbar();
  const [tabIndex, setTabIndex] = useState(0);

  const [autoReplyRulesCount, setAutoReplyRulesCount] = useState(0);
  // Support KB document count, driven by the KnowledgeBaseManager instance below.
  // Gates the auto-reply enable switch (which is about the support KB).
  const [supportDocCount, setSupportDocCount] = useState(0);

  const dataAPI = useContext(DataAPIContext);

  // Index = tab value. Display order differs (AI TRAINING INBOX sits second), values stay stable.
  const TAB_KEYS = ['training-files', 'auto-tagging', 'metrics', 'training-inbox', 'reminder-gaps'];

  const {
    hasPerm, updateTourOperator, tourOperator, isFranchise, currentLicensee, licensees,
  } = React.useContext(ContextStore);
  const hasAIlistPerm = hasPerm('ai', 'list')
  // Metrics are visible to anyone with access to the AI pages (same gate as the page
  // itself). Was temporarily restricted to help@curatedplanet.com during rollout.
  const canSeeMetrics = hasAIlistPerm;
  const hasAIdeletePerm = hasPerm('ai', 'delete')
  const hasAIwritePerm = hasPerm('ai', 'write')

  // Franchise: per-licensee AI settings
  const [selectedLicensee, setSelectedLicensee] = useState(currentLicensee || null);
  const effectiveTopId = (isFranchise && selectedLicensee) ? selectedLicensee : tourOperator?.id;

  // Web-widget entitlement gates the KB channel switch + copy action (same flag the
  // auto-reply rules table uses for its source-scope UI). When off, everything stays
  // support-only and the operator sees no change.
  const webWidgetAvailable = isWebWidgetAvailable(tourOperator);

  // Knowledge Review ("AI TRAINING INBOX", support chat; the widget has its own on the Web Chat
  // Widget page). Always visible outside production; in production only for operators with
  // `knowledge_review_enabled` (client previews). Sample data unless `knowledge_review_live`.
  // A franchise switches it on per licensee: the picked licensee's switches decide.
  const inboxOperator = inboxOperatorFor(tourOperator, isFranchise, licensees, selectedLicensee);
  const canSeeReview = hasAIlistPerm && trainingInboxVisibleFor(inboxOperator);
  // Picking a licensee whose inbox is off hides the tab: leave it rather than show an empty panel.
  useEffect(() => { if (tabIndex === 3 && !canSeeReview) setTabIndex(0); }, [tabIndex, canSeeReview]);
  // Reminder gaps: sample data outside production; real data (and, in production, the tab at all)
  // when `reminder_gaps_enabled` is true, or "staff" for the staff accounts (alpha).
  const canSeeReminderGaps = hasAIlistPerm && reminderGapsVisibleFor(tourOperator);
  const reminderGapsLive = reminderGapsLiveFor(tourOperator);
  const [reminderGapsCount, setReminderGapsCount] = useState(null);
  const [reviewCount, setReviewCount] = useState(null);
  // The main menu's AI Suite badge shows the same number.
  useEffect(() => { setTrainingInboxCount(effectiveTopId, 'support', reviewCount); }, [effectiveTopId, reviewCount]);

  const getTabFromLocation = () => {
    const hash = (window.location.hash || '').replace(/^#/, '');
    const qs = new URLSearchParams(window.location.search);
    const qstab = (qs.get('tab') || '').toLowerCase();
    const key = (hash || qstab || '').toLowerCase();
    const idx = TAB_KEYS.indexOf(key);
    if (idx === 3 && !canSeeReview) return 0;
    if (idx === 4 && !canSeeReminderGaps) return 0;
    return idx >= 0 ? idx : 0;
  };

  // ----- Chatbot settings (auto-reply) -----
  const bootstrap = useMemo(() => {
    try { return JSON.parse(window.localStorage.getItem('bootstrap') || '{}'); } catch { return {}; }
  }, []);
  const tourOpId = bootstrap?.tour_operator?.id;
  const initialConfig = bootstrap?.tour_operator?.config || {};

  const [autoReplyEnabled, setAutoReplyEnabled] = useState(!!initialConfig.auto_reply_with_ai);
  // Scope mode controls how the lists below are interpreted: "exclude" (send
  // everywhere except the listed items — default) or "include" (send only for the
  // listed items). The id lists themselves are shared between the two modes.
  const [scopeMode, setScopeMode] = useState(
    initialConfig.auto_reply_scope_mode === 'include' ? 'include' : 'exclude'
  );
  // Tour/system-tag/participant-tag lists for auto-SEND scoping. Stored as
  // normalized {id, name} objects in state; persisted as integer id arrays.
  const [excludeTours, setExcludeTours] = useState([]);
  const [excludeSystemTags, setExcludeSystemTags] = useState([]);
  const [excludeParticipantTags, setExcludeParticipantTags] = useState([]);
  const [excludeTourQuery, setExcludeTourQuery] = useState('');
  const [excludeTourOptions, setExcludeTourOptions] = useState([]);
  const [excludeParticipantTagQuery, setExcludeParticipantTagQuery] = useState('');
  const [excludeParticipantTagOptions, setExcludeParticipantTagOptions] = useState([]);
  // Message sources the AI skips entirely (suggestion + auto-reply). Absent from config
  // => the backend default applies, so show that rather than an empty box.
  const [excludeSourcePlatforms, setExcludeSourcePlatforms] = useState(
    Array.isArray(initialConfig.ai_exclude_source_platforms)
      ? initialConfig.ai_exclude_source_platforms
      : DEFAULT_AI_EXCLUDED_SOURCE_PLATFORMS
  );
  const [learnScope, setLearnScope] = useState(initialConfig.ai_learning_scope === 'selected' ? 'selected' : 'all');
  const [learnUsers, setLearnUsers] = useState([]);
  // Missed-tour rebooking offers: the card shows only once the platform team has made the
  // feature available for this operator (the config key exists).
  const [rebookingConfig, setRebookingConfig] = useState(initialConfig.ai_missed_tour_rebooking ?? null);
  const [useSuggestedResponses, setUseSuggestedResponses] = useState(!!initialConfig.auto_populate_suggested_response);
  const [pauseAfterManual, setPauseAfterManual] = useState(!!initialConfig.pause_chatbot_for_x_minutes_after_manual_response);
  const [shouldAutoCloseChatIssues, setShouldAutoCloseChatIssues] = useState(!!initialConfig.ai_auto_close_chat_issues);
  const [pauseMinutes, setPauseMinutes] = useState(
    Number.isFinite(initialConfig.pause_chatbot_for_x_minutes_after_manual_response) ? initialConfig.pause_chatbot_for_x_minutes_after_manual_response : 5
  );
  const [chatbotPrompt, setChatbotPrompt] = useState(initialConfig.chatbot_prompt || '');
  const [chatbotAiDisclosure, setChatbotAiDisclosure] = useState(initialConfig.chatbot_ai_disclosure || '');
  // Per-language disclosures stored as a sorted array of {lang, text} for stable rendering;
  // serialised to/from the backend's `chatbot_ai_disclosure_translations` dict on save/load.
  const translationsDictToRows = (dict) => {
    // LANGUAGE_MAP maps a code to a { code, name } object, so sort by its name
    // (falling back to the raw code); guard to a string so localeCompare is safe.
    const langLabel = (code) => String((LANGUAGE_MAP[code] && LANGUAGE_MAP[code].name) || code || '');
    return Object.entries(dict || {})
      .map(([lang, text]) => ({ lang, text }))
      .sort((a, b) => langLabel(a.lang).localeCompare(langLabel(b.lang)));
  };
  const [chatbotAiDisclosureTranslations, setChatbotAiDisclosureTranslations] = useState(
    translationsDictToRows(initialConfig.chatbot_ai_disclosure_translations)
  );
  const [savingPromptSettings, setSavingPromptSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  // Last loaded/saved values of the AI Response Customization card -- the one card on this page
  // with an explicit SAVE (everything else saves on change). SAVE stays off until one of them
  // differs, and leaving the page with unsaved edits asks first (CP-8423). No CANCEL on purpose:
  // it would read as undoing the whole page.
  const [loadedPromptSettings, setLoadedPromptSettings] = useState(() => ({
    prompt: initialConfig.chatbot_prompt || '',
    disclosure: initialConfig.chatbot_ai_disclosure || '',
    translations: translationsDictToRows(initialConfig.chatbot_ai_disclosure_translations),
  }));
  const promptSettingsChanged =
    chatbotPrompt !== loadedPromptSettings.prompt ||
    chatbotAiDisclosure !== loadedPromptSettings.disclosure ||
    JSON.stringify(chatbotAiDisclosureTranslations) !== JSON.stringify(loadedPromptSettings.translations);

  // Gate on SUPPORT KB presence or any auto-reply rule.
  const hasKBFilesOrRules = supportDocCount > 0 || autoReplyRulesCount > 0;
  const canEnableAutoReply = hasAIwritePerm && hasKBFilesOrRules;

  // global min confidence (0..1), slider uses 0..100 view
  const initialMinConf = Number.isFinite(initialConfig.ai_auto_reply_min_confidence)
    ? Number(initialConfig.ai_auto_reply_min_confidence)
    : 0.85;
  const [minConfidence, setMinConfidence] = useState(
    Math.max(0, Math.min(1, initialMinConf))
  );
  const [pendingMinConf, setPendingMinConf] = useState(minConfidence); // for debounced save

  // auto-close confidence (0..1), separate from auto-reply threshold
  const initialAutoCloseConf = Number.isFinite(initialConfig.ai_auto_close_min_confidence)
    ? Number(initialConfig.ai_auto_close_min_confidence)
    : 0.85;
  const [autoCloseMinConf, setAutoCloseMinConf] = useState(
    Math.max(0, Math.min(1, initialAutoCloseConf))
  );
  const [pendingAutoCloseConf, setPendingAutoCloseConf] = useState(autoCloseMinConf);

  const updateBootstrapConfig = (patch) => {
    try {
      const curr = JSON.parse(localStorage.getItem('bootstrap') || '{}');
      const next = {
        ...curr,
        tour_operator: {
          ...(curr.tour_operator || {}),
          config: { ...(curr.tour_operator?.config || {}), ...patch },
        },
      };
      localStorage.setItem('bootstrap', JSON.stringify(next));
    } catch { }
  };

  const saveTourOperatorConfig = async (patch) => {
    if (!effectiveTopId) return false;
    setSavingSettings(true);
    try {
      // The data provider does not throw on 4xx — it returns { statusCode, data }.
      // The backend rejects some config changes with a message (e.g. enabling AI
      // replies with no knowledge base documents and no auto-reply rules).
      const res = await dataAPI?.(UPDATE, 'tour_operator', { id: effectiveTopId, data: { config: patch } });
      if (res?.statusCode >= 400) {
        enqueueSnackbar(res?.data?.message || 'Failed to update chatbot settings', { variant: 'error' });
        return false;
      }
      updateBootstrapConfig(patch);
      // Sync notification sound settings to IndexedDB for service worker
      if (patch.background_dashboard_chat_notification_sound_enabled !== undefined) {
        IndexedDB_set('notificationSoundEnabledBackground', !!patch.background_dashboard_chat_notification_sound_enabled);
      }
      if (patch.foreground_dashboard_chat_notification_sound_enabled !== undefined) {
        IndexedDB_set('notificationSoundEnabledForeground', !!patch.foreground_dashboard_chat_notification_sound_enabled);
      }
      enqueueSnackbar('Chatbot settings updated', { variant: 'success' });
      return true;
    } catch (e) {
      enqueueSnackbar('Failed to update chatbot settings', { variant: 'error' });
      return false;
    } finally {
      setSavingSettings(false);
    }
  };

  const savePromptSettings = async () => {
    if (!effectiveTopId) return;
    setSavingPromptSettings(true);
    try {
      // Drop blank/duplicate rows; backend accepts only string→string entries.
      const translationsDict = {};
      chatbotAiDisclosureTranslations.forEach((row) => {
        const lang = (row.lang || '').trim().toLowerCase();
        const text = (row.text || '').trim();
        if (lang && text) translationsDict[lang] = row.text;
      });
      const patch = {
        chatbot_prompt: chatbotPrompt,
        chatbot_ai_disclosure: chatbotAiDisclosure,
        chatbot_ai_disclosure_translations: translationsDict,
      };
      await dataAPI?.(UPDATE, 'tour_operator', { id: effectiveTopId, data: { config: patch } });
      updateBootstrapConfig(patch);
      setLoadedPromptSettings({
        prompt: chatbotPrompt,
        disclosure: chatbotAiDisclosure,
        translations: chatbotAiDisclosureTranslations,
      });
      enqueueSnackbar('Auto-response settings saved', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to save auto-response settings', { variant: 'error' });
    } finally {
      setSavingPromptSettings(false);
    }
  };

  const toggleAutoReply = async (checked) => {
    setAutoReplyEnabled(checked);
    const ok = await saveTourOperatorConfig({ auto_reply_with_ai: checked });
    if (!ok) setAutoReplyEnabled(!checked);
  };

  // Persist the auto-send exclusion lists as integer id arrays. The backend
  // validator drops the field unless every entry is an int, so coerce + filter.
  const toIntIds = (items, idOf) =>
    (items || []).map((it) => parseInt(idOf(it), 10)).filter((n) => Number.isInteger(n));
  const persistAutoReplyExclusions = async (tours, systemTags, participantTags) => {
    await saveTourOperatorConfig({
      auto_reply_exclude_tour_ids: toIntIds(tours, tourToId),
      auto_reply_exclude_system_tag_ids: toIntIds(systemTags, tagToId),
      auto_reply_exclude_participant_tag_ids: toIntIds(participantTags, tagToId),
    });
  };

  const onExcludeToursChange = (v) => {
    const next = (v || []).map(normalizeTour);
    setExcludeTours(next);
    persistAutoReplyExclusions(next, excludeSystemTags, excludeParticipantTags);
  };

  const onExcludeSystemTagsChange = (v) => {
    // Keep raw tag objects (they carry `category` for SystemTagsDropdown's label).
    const next = v || [];
    setExcludeSystemTags(next);
    persistAutoReplyExclusions(excludeTours, next, excludeParticipantTags);
  };

  const onExcludeParticipantTagsChange = (v) => {
    const next = (v || []).map(normalizeTag);
    setExcludeParticipantTags(next);
    persistAutoReplyExclusions(excludeTours, excludeSystemTags, next);
  };

  const onExcludeSourcePlatformsChange = (v) => {
    // Options are objects while typed/selected values come back as {value,label};
    // persist the bare source_platform strings the backend matches on.
    const next = (v || []).map((it) => (typeof it === 'string' ? it : it?.value)).filter(Boolean);
    setExcludeSourcePlatforms(next);
    saveTourOperatorConfig({ ai_exclude_source_platforms: next });
  };

  const onLearnScopeChange = (mode) => {
    const next = mode === 'selected' ? 'selected' : 'all';
    setLearnScope(next);
    saveTourOperatorConfig({ ai_learning_scope: next });
  };

  const onLearnUsersChange = (v) => {
    const next = v || [];
    setLearnUsers(next);
    saveTourOperatorConfig({ ai_learning_user_ids: toIntIds(next, (u) => u.id) });
  };

  const onScopeModeChange = (mode) => {
    const next = mode === 'include' ? 'include' : 'exclude';
    setScopeMode(next);
    saveTourOperatorConfig({ auto_reply_scope_mode: next });
  };

  const toggleUseSuggestedResponses = async (checked) => {
    setUseSuggestedResponses(checked);
    const ok = await saveTourOperatorConfig({ auto_populate_suggested_response: checked });
    if (!ok) setUseSuggestedResponses(!checked);
  };

    const togglePauseAfterManual = async (checked) => {
      setPauseAfterManual(checked);
      // If turning on and pauseMinutes is 0 or invalid, set to default of 5 minutes
      const minutesToSave = checked
        ? (PAUSE_AFTER_MANUAL_OPTIONS.includes(pauseMinutes) ? pauseMinutes : 5)
        : 0;
      if (checked && minutesToSave !== pauseMinutes) {
        setPauseMinutes(minutesToSave);
      }
      await saveTourOperatorConfig({ pause_chatbot_for_x_minutes_after_manual_response: minutesToSave });
    };

  const toggleShouldAutoCloseChatIssues = async (checked) => {
    setShouldAutoCloseChatIssues(checked);
    await saveTourOperatorConfig({ ai_auto_close_chat_issues: checked });
  };

  const changePauseMinutes = async (mins) => {
    setPauseMinutes(mins);
    await saveTourOperatorConfig({ pause_chatbot_for_x_minutes_after_manual_response: mins });
  };

  // debounced saver for confidence slider
  useEffect(() => {
    const t = setTimeout(() => {
      if (pendingMinConf !== minConfidence) {
        setMinConfidence(pendingMinConf);
        saveTourOperatorConfig({ ai_auto_reply_min_confidence: pendingMinConf });
      }
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMinConf]);

  // debounced saver for auto-close confidence slider
  useEffect(() => {
    const t = setTimeout(() => {
      if (pendingAutoCloseConf !== autoCloseMinConf) {
        setAutoCloseMinConf(pendingAutoCloseConf);
        saveTourOperatorConfig({ ai_auto_close_min_confidence: pendingAutoCloseConf });
      }
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoCloseConf]);


  useEffect(() => {
    setTabIndex(getTabFromLocation());
    const onPop = () => setTabIndex(getTabFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Refresh tour_operator config when page is accessed to get latest changes
  useEffect(() => {
    if (!tourOperator?.id || !dataAPI) return;

    let active = true;
    dataAPI(GET_ONE, 'tour_operator', { id: tourOperator.id })
      .then((res) => {
        // A refused request resolves with its body ({message}, or [] for an error page)
        if (active && isOk(res) && res.data) {
          updateTourOperator(res.data);
          // Also update localStorage bootstrap
          updateBootstrapConfig(res.data.config || {});
          const cfg = res.data.config || {};
          if (cfg.chatbot_prompt != null) setChatbotPrompt(cfg.chatbot_prompt);
          if (cfg.chatbot_ai_disclosure != null) setChatbotAiDisclosure(cfg.chatbot_ai_disclosure);
          if (cfg.chatbot_ai_disclosure_translations != null) {
            setChatbotAiDisclosureTranslations(translationsDictToRows(cfg.chatbot_ai_disclosure_translations));
          }
          setLoadedPromptSettings((prev) => ({
            prompt: cfg.chatbot_prompt != null ? cfg.chatbot_prompt : prev.prompt,
            disclosure: cfg.chatbot_ai_disclosure != null ? cfg.chatbot_ai_disclosure : prev.disclosure,
            translations: cfg.chatbot_ai_disclosure_translations != null
              ? translationsDictToRows(cfg.chatbot_ai_disclosure_translations)
              : prev.translations,
          }));
        }
      })
      .catch((err) => {
        // Silently fail - don't interrupt user experience
        console.warn('Failed to refresh tour operator on page load:', err);
      });

    return () => {
      active = false;
    };
  }, [tourOperator?.id, dataAPI, updateTourOperator]);

  // Badge count for the AI TRAINING INBOX tab before it is opened; the tab keeps it fresh after.
  useEffect(() => {
    if (!canSeeReview || !effectiveTopId) return;
    let active = true;
    getKnowledgeReviewApi(dataAPI, effectiveTopId, isLiveFor(inboxOperator)).summary('support')
      .then((s) => { if (active) setReviewCount(s?.open ?? null); })
      .catch(() => { });
    return () => { active = false; };
  }, [canSeeReview, effectiveTopId, dataAPI, inboxOperator]);

  // Badge count for the REMINDER GAPS tab (findings to review) before it is opened; the tab keeps it fresh after.
  useEffect(() => {
    if (!canSeeReminderGaps || !effectiveTopId) return;
    let active = true;
    const api = reminderGapsLive ? reminderGapsApi(dataAPI, effectiveTopId) : createReminderGapsMockApi();
    api.summary()
      .then((s) => { if (active) setReminderGapsCount(s?.counts?.open ?? null); })
      .catch(() => { });
    return () => { active = false; };
  }, [canSeeReminderGaps, reminderGapsLive, effectiveTopId, dataAPI]);

  const tabsHeader = (
    <Tabs value={tabIndex} onChange={(e, v) => { setTabIndex(v); pushTabToUrl(v); }} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" aria-label="Training materials tabs">
      <Tab label="AUTO-RESPONSES" {...a11yProps(0)} />
      {canSeeReview && (
        <Tab
          {...a11yProps(3)}
          label={(
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              AI TRAINING INBOX
              {reviewCount > 0 && <Chip label={reviewCount} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />}
            </Box>
          )}
        />
      )}
      {canSeeReminderGaps && (
        <Tab
          {...a11yProps(4)}
          label={(
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              REMINDER GAPS
              {reminderGapsCount > 0 && <Chip label={reminderGapsCount} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />}
            </Box>
          )}
        />
      )}
      <Tab label="AUTO-TAGGING" {...a11yProps(1)} />
      {canSeeMetrics && <Tab label="METRICS" {...a11yProps(2)} />}
    </Tabs>
  );

  const pushTabToUrl = (idx) => {
    const key = TAB_KEYS[idx] || TAB_KEYS[0];
    const url = new URL(window.location.href);
    // prefer hash for readability; also keep ?tab in sync
    url.hash = `#${key}`;
    url.searchParams.set('tab', key);
    window.history.pushState({}, '', url.toString());
  };

  // Reload tour operator config when licensee changes
  useEffect(() => {
    if (!effectiveTopId) return;
    let active = true;
    (async () => {
      try {
        const res = await dataAPI?.(GET_ONE, 'tour_operator', { id: effectiveTopId });
        if (!active || !res?.data?.config) return;
        const cfg = res.data.config;
        setAutoReplyEnabled(!!cfg.auto_reply_with_ai);
        setUseSuggestedResponses(!!cfg.auto_populate_suggested_response);
        setPauseAfterManual(!!cfg.pause_chatbot_for_x_minutes_after_manual_response);
        setShouldAutoCloseChatIssues(!!cfg.ai_auto_close_chat_issues);
        setPauseMinutes(Number.isFinite(cfg.pause_chatbot_for_x_minutes_after_manual_response) ? cfg.pause_chatbot_for_x_minutes_after_manual_response : 5);
        setChatbotPrompt(cfg.chatbot_prompt || '');
        setChatbotAiDisclosure(cfg.chatbot_ai_disclosure || '');
        setChatbotAiDisclosureTranslations(translationsDictToRows(cfg.chatbot_ai_disclosure_translations));
        setLoadedPromptSettings({
          prompt: cfg.chatbot_prompt || '',
          disclosure: cfg.chatbot_ai_disclosure || '',
          translations: translationsDictToRows(cfg.chatbot_ai_disclosure_translations),
        });
        const conf = Number.isFinite(cfg.ai_auto_reply_min_confidence) ? cfg.ai_auto_reply_min_confidence : 0.85;
        setMinConfidence(Math.max(0, Math.min(1, conf)));
        setPendingMinConf(Math.max(0, Math.min(1, conf)));
        const acConf = Number.isFinite(cfg.ai_auto_close_min_confidence) ? cfg.ai_auto_close_min_confidence : 0.85;
        setAutoCloseMinConf(Math.max(0, Math.min(1, acConf)));
        setPendingAutoCloseConf(Math.max(0, Math.min(1, acConf)));
        setScopeMode(cfg.auto_reply_scope_mode === 'include' ? 'include' : 'exclude');
        setLearnScope(cfg.ai_learning_scope === 'selected' ? 'selected' : 'all');
        setRebookingConfig(cfg.ai_missed_tour_rebooking ?? null);
        setExcludeSourcePlatforms(
          Array.isArray(cfg.ai_exclude_source_platforms)
            ? cfg.ai_exclude_source_platforms
            : DEFAULT_AI_EXCLUDED_SOURCE_PLATFORMS
        );

        // Resolve tour/tag ids -> {id, name} objects for chip display.
        const resolveIds = async (ids, resource, normalizer, setter) => {
          if (!Array.isArray(ids) || !ids.length) { if (active) setter([]); return; }
          try {
            const r = await dataAPI?.(GET_MANY, resource, { ids });
            if (active) setter((r?.data || []).map(normalizer));
          } catch { if (active) setter(ids.map((id) => ({ id, name: `#${id}` }))); }
        };
        // Team members one by one, so a since-deleted user shows as "User #id" (removable)
        // instead of failing the whole list.
        const resolveTeamMembers = async (ids) => {
          if (!Array.isArray(ids) || !ids.length) { if (active) setLearnUsers([]); return; }
          const results = await Promise.allSettled(ids.map((id) => dataAPI?.(GET_ONE, 'user', { id })));
          if (!active) return;
          setLearnUsers(results.map((r, i) => (
            r.status === 'fulfilled' && r.value?.data?.id != null ? normalizeTeamMember(r.value.data) : { id: ids[i], display_name: '', email: '' }
          )));
        };
        await Promise.all([
          resolveTeamMembers(cfg.ai_learning_user_ids),
          resolveIds(cfg.auto_reply_exclude_tour_ids, TOURS_RESOURCE, normalizeTour, setExcludeTours),
          resolveIds(cfg.auto_reply_exclude_system_tag_ids, SYSTEM_TAGS_RESOURCE, (x) => x, setExcludeSystemTags),
          resolveIds(cfg.auto_reply_exclude_participant_tag_ids, TAGS_RESOURCE, normalizeTag, setExcludeParticipantTags),
        ]);
      } catch { }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTopId]);

  return hasAIlistPerm ? (
    <FormLicenseeContext.Provider value={effectiveTopId}>
    <BlockRedirect shouldBlock={hasAIwritePerm && promptSettingsChanged && !savingPromptSettings} message={LEAVE_WITHOUT_SAVING_MESSAGE} />
    <Box p={2}>
      <PageMeta title="AI Suite" />
      <PageHeading sx={{ mb: 1, textAlign: 'left' }}>AI Suite</PageHeading>
      <Box mb={2} maxWidth={400}>
        <LicenseeFormField
          value={selectedLicensee}
          onChange={setSelectedLicensee}
          error={isFranchise && !selectedLicensee}
          helperText={isFranchise && !selectedLicensee ? 'Please select a licensee or the parent operator' : undefined}
          allowParent={hasPerm('franchise', 'manager')}
        />
      </Box>

      {isFranchise && !selectedLicensee ? null : <><TabPanel value={tabIndex} index={0}>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          {tabsHeader}
          <Divider />
          {/* Chatbot settings */}
          <Box sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0}>
            <Typography variant="h6">Chatbot settings</Typography>
          </Box>

          {/* Subtitle */}
          <Typography variant="body2" sx={{ mb: 2 }}>
          Configure response behavior, confidence threshold, and training options for your chatbot.
          </Typography>

          {/* Enable auto-reply row */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, mb: 1 }}>
              <Tooltip
                title={!canEnableAutoReply && !autoReplyEnabled ? 'Upload at least one knowledge base file or create an auto-reply rule first' : ''}
                arrow
                placement="right"
              >
                <span>
                  <Switch
                    checked={autoReplyEnabled}
                    onChange={(e) => toggleAutoReply(e.target.checked)}
                    disabled={savingSettings || !hasAIwritePerm || (!autoReplyEnabled && !canEnableAutoReply)}
                  />
                </span>
              </Tooltip>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  Enable auto-reply
                </Typography>
                <Typography variant="caption">
                  Automatically send AI-generated replies to guests when confidence is above the threshold.
                  Guests whose notifications are paused only get suggested replies.
                </Typography>
              </Box>
            </Box>

            {/* Auto-reply confidence threshold */}
            <Box sx={{ pl: { xs: 1, sm: 9.75 }, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Auto-Reply Confidence Threshold
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                Adjust the minimum confidence level required for the chatbot to automatically send a reply without human review.
              </Typography>
              <Box sx={{ pr: 1, pl: { xs: 1.5, sm: 0 }, maxWidth: 520, marginTop: 4 }}>
                <Slider
                  value={Math.round((pendingMinConf ?? minConfidence) * 100)}
                  onChange={(_, v) => setPendingMinConf((Number(v) || 0) / 100)}
                  valueLabelDisplay="on"
                  valueLabelFormat={(v) => `${v}%`}
                  min={0}
                  max={100}
                  step={1}
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  sx={{
                    '& .MuiSlider-valueLabel': {
                      px: 1,
                      py: 0.25,
                    },
                    // Restricted zone: the slider can't go below the floor, so we hatch
                    // out the 0–80% region (with a boundary line at the floor) directly
                    // on top of the bar. Drawn as a pseudo-element on the slider so it
                    // shares the rail's geometry and stays perfectly aligned.
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: `${Math.round(AUTO_REPLY_CONFIDENCE_FLOOR * 100)}%`,
                      height: 6,
                      borderRadius: 3,
                      pointerEvents: 'none',
                      zIndex: 1,
                      backgroundColor: 'action.disabledBackground',
                      backgroundImage:
                        'repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0, rgba(0,0,0,0.22) 3px, transparent 3px, transparent 7px)',
                      borderRight: '2px solid',
                      borderColor: 'text.disabled',
                    },
                  }}
                />
                <Typography variant="caption" component="div" sx={{ mt: -0.3, textAlign: 'left' }}>
                  {/* On phones the two clauses stack vertically (CP-7098) */}
                  <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    <strong>Above {Math.round((pendingMinConf ?? minConfidence) * 100)}%</strong> → auto-reply.
                  </Box>
                  {' '}
                  <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    <strong>Below {Math.round((pendingMinConf ?? minConfidence) * 100)}%</strong> → suggest reply (if enabled).
                  </Box>
                </Typography>
              </Box>
            </Box>

            {/* Auto-reply scope: send everywhere except a blocklist, or only for a chosen set */}
            <Box sx={{ pl: { xs: 1, sm: 9.75 }, mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Auto-reply scope
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                Choose whether auto-replies go everywhere except a blocklist, or only to a chosen set of tours / tags / participants. Suggested replies (if enabled) are unaffected.
              </Typography>
              <RadioGroup
                row
                value={scopeMode}
                onChange={(e) => onScopeModeChange(e.target.value)}
                sx={{ gap: { xs: 0.5, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <FormControlLabel
                  value="exclude"
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: scopeMode === 'exclude' ? 600 : 400, color: scopeMode === 'exclude' ? 'text.primary' : 'text.disabled' }}>Send everywhere except for selected tours / tags</Typography>
                  }
                />
                <FormControlLabel
                  value="include"
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: scopeMode === 'include' ? 600 : 400, color: scopeMode === 'include' ? 'text.primary' : 'text.disabled' }}>Only send for selected tours / tags</Typography>
                  }
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1.5 }}>
                {scopeMode === 'include'
                  ? 'Auto-replies go only to conversations matching the tours, system tags, or participant tags below (any match qualifies).'
                  : 'Auto-replies go to all conversations except the tours, system tags, or participant tags below (any match excludes).'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
                <RemoteAutocomplete
                  multiple
                  resource={TOURS_RESOURCE}
                  apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                  value={excludeTours}
                  onChange={(e, v) => onExcludeToursChange(v)}
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  setOptionsCount={() => {}}
                  setOptions={(opts) => setExcludeTourOptions((opts || []).map(normalizeTour))}
                  filterOptions={(options) => options}
                  fullWidth
                  size="small"
                  label={scopeMode === 'include' ? 'Included tours' : 'Excluded tours'}
                  variant="outlined"
                  clearOnBlur={false}
                  noOptionsText={excludeTourQuery.trim() ? `No results for "${excludeTourQuery.trim()}"` : 'Type to search'}
                  onQueryChange={(q) => setExcludeTourQuery(q)}
                  getOptionLabel={(opt) => tourToLabel(opt)}
                  isOptionEqualToValue={(opt, val) => toursEqual(opt, val)}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => (
                      <Chip {...getTagProps({ index: i })} size="small" label={tourToLabel(opt)} />
                    ))
                  }
                />
                <SystemTagsDropdown
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  label={scopeMode === 'include' ? 'Included system tags' : 'Excluded system tags'}
                  variant="outlined"
                  value={excludeSystemTags}
                  onChange={(e, v) => onExcludeSystemTagsChange(v)}
                />
                <RemoteAutocomplete
                  multiple
                  resource={TAGS_RESOURCE}
                  apiFilters={[{ field: 'category_code', op: 'not', value: INTERNAL_TAG_CATEGORY_CODE }]}
                  value={excludeParticipantTags}
                  onChange={(e, v) => onExcludeParticipantTagsChange(v)}
                  disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                  setOptionsCount={() => {}}
                  setOptions={(opts) => setExcludeParticipantTagOptions((opts || []).map(normalizeTag))}
                  fullWidth
                  size="small"
                  label={scopeMode === 'include' ? 'Included participant tags' : 'Excluded participant tags'}
                  variant="outlined"
                  clearOnBlur={false}
                  noOptionsText={excludeParticipantTagQuery.trim() ? `No results for "${excludeParticipantTagQuery.trim()}"` : 'Type to search'}
                  renderOption={renderBoldTagOption}
                  onQueryChange={(q) => setExcludeParticipantTagQuery(q)}
                  inputValue={excludeParticipantTagQuery}
                  onInputChange={(e, value) => setExcludeParticipantTagQuery(value)}
                  getOptionLabel={(opt) => tagToName(opt)}
                  isOptionEqualToValue={(opt, val) => tagsEqual(opt, val)}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => (
                      <Chip {...getTagProps({ index: i })} size="small" label={tagToName(opt)} />
                    ))
                  }
                />
                {excludeTours.length === 0 && excludeSystemTags.length === 0 && excludeParticipantTags.length === 0 && (
                  scopeMode === 'include' ? (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      Nothing selected — auto-reply won't be sent anywhere until you add at least one tour, system tag, or participant tag.
                    </Alert>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No exclusions — auto-reply active everywhere</Typography>
                  )
                )}
              </Box>
            </Box>
          </Box>

          {/* Use suggested responses row */}
          <Box sx={{ mt: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, mb: 1 }}>
              <Tooltip
                title={!canEnableAutoReply && !useSuggestedResponses ? 'Upload at least one knowledge base file or create an auto-reply rule first' : ''}
                arrow
                placement="right"
              >
                <span>
                  <Switch
                    checked={useSuggestedResponses}
                    onChange={(e) => toggleUseSuggestedResponses(e.target.checked)}
                    disabled={savingSettings || !hasAIwritePerm || (!useSuggestedResponses && !canEnableAutoReply)}
                  />
                </span>
              </Tooltip>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  Use suggested responses
                </Typography>
                <Typography variant="caption">
                  Allow chatbot to suggest replies to the operator when auto-reply is disabled or when auto-reply confidence is below the threshold.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Pause after manual response row */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, mb: 1 }}>
              <Switch
                checked={pauseAfterManual}
                onChange={(e) => togglePauseAfterManual(e.target.checked)}
                disabled={savingSettings || !autoReplyEnabled || !hasAIwritePerm}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  Pause auto-reply after manual response
                </Typography>
                <Typography variant="caption">
                  This prevents the automated system from interfering with personalized interactions.
                  Unlimited keeps the bot off for the rest of the conversation.
                </Typography>
              </Box>
            </Box>
            {/* Duration select */}
            <Box sx={{
              pt: 1, 
              ml: (theme) =>
                // pl:0.5  +  switch width (~6 spacing) + gap:2
                `calc(${theme.spacing(1
                  
                )} + ${theme.spacing(6)} + ${theme.spacing(2)})`, 
                minWidth: 140,
                maxWidth: 160
            }}>
              <FormControl fullWidth size="small" disabled={!pauseAfterManual || savingSettings || !autoReplyEnabled || !hasAIwritePerm}>
                <InputLabel id="pause-duration-label">Pause auto-reply for</InputLabel>
                <Select
                  labelId="pause-duration-label"
                  label="Pause auto-reply for"
                  size="small"
                  fullWidth
                  value={pauseAfterManual && PAUSE_AFTER_MANUAL_OPTIONS.includes(pauseMinutes) ? pauseMinutes : 5}
                  onChange={(e) => changePauseMinutes(Number(e.target.value))}
                  disabled={!pauseAfterManual || savingSettings || !autoReplyEnabled || !hasAIwritePerm}
                >
                  {PAUSE_AFTER_MANUAL_OPTIONS.map((m) => (
                    <MenuItem key={m} value={m}>{pauseAfterManualLabel(m)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Should auto close chat issues row */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, mb: 1 }}>
              <Switch
                checked={shouldAutoCloseChatIssues}
                onChange={(e) => toggleShouldAutoCloseChatIssues(e.target.checked)}
                disabled={savingSettings || !hasAIwritePerm}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  Mark conversations as auto-closed when resolved
                </Typography>
                <Typography variant="caption">
                  Use AI to automatically mark conversations as auto-closed when a guest's reply indicates that their issues have been resolved (e.g. "okay", "thank you", "no problem", etc.).
                </Typography>
              </Box>
            </Box>

            {/* Auto-close confidence threshold */}
            <Box sx={{ pl: { xs: 1, sm: 9.75 }, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Auto-Close Confidence Threshold
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                Adjust the minimum confidence level required for the AI to automatically mark a conversation as closed.
              </Typography>
              <Box sx={{ pr: 1, pl: { xs: 1.5, sm: 0 }, maxWidth: 520, marginTop: 4 }}>
                <Slider
                  value={Math.round((pendingAutoCloseConf ?? autoCloseMinConf) * 100)}
                  onChange={(_, v) => setPendingAutoCloseConf((Number(v) || 0) / 100)}
                  valueLabelDisplay="on"
                  valueLabelFormat={(v) => `${v}%`}
                  min={0}
                  max={100}
                  step={1}
                  disabled={savingSettings || !shouldAutoCloseChatIssues || !hasAIwritePerm}
                  sx={{
                    '& .MuiSlider-valueLabel': {
                      px: 1,
                      py: 0.25,
                    },
                  }}
                />
                <Typography variant="caption" component="div" sx={{ mt: -0.3, textAlign: 'left' }}>
                  {/* On phones the two clauses stack vertically (CP-7098) */}
                  <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    <strong>Above {Math.round((pendingAutoCloseConf ?? autoCloseMinConf) * 100)}%</strong> → auto-close (if enabled).
                  </Box>
                  {' '}
                  <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    <strong>Below {Math.round((pendingAutoCloseConf ?? autoCloseMinConf) * 100)}%</strong> → leave open for review.
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Section-wide kill switch: an excluded source gets no auto-reply, no suggested
              reply and no auto-close, so it belongs at the end of the section governing
              all three rather than nested under any one of them. Distinct from the
              auto-reply scope lists above, which only block the send. */}
          {/* Indented to the same gutter as the other sub-settings (Auto-Reply /
              Auto-Close Confidence Threshold), which clear the switch column. */}
          <Box sx={{ mb: 1, pl: { xs: 1, sm: 9.75 }, maxWidth: 520 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Excluded message sources
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5 }}>
              Applies to everything above: messages from these sources get no AI treatment at all —
              no auto-reply, no suggested reply, and no auto-close. Use it for sources that reach you
              without their surrounding conversation, such as Airbnb group threads, where a guest's
              "Me too" arrives without the message they were answering.
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={AI_SOURCE_PLATFORM_OPTIONS}
              value={excludeSourcePlatforms.map(
                (v) => AI_SOURCE_PLATFORM_OPTIONS.find((o) => o.value === v) || { value: v, label: v }
              )}
              onChange={(e, v) => onExcludeSourcePlatformsChange(v)}
              disabled={savingSettings || !hasAIwritePerm}
              getOptionLabel={(opt) => (typeof opt === 'string' ? sourcePlatformLabel(opt) : opt.label)}
              isOptionEqualToValue={(opt, val) => opt.value === (typeof val === 'string' ? val : val?.value)}
              renderTags={(value, getTagProps) =>
                value.map((opt, i) => (
                  <Chip {...getTagProps({ index: i })} size="small" label={opt.label} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Excluded message sources" />
              )}
            />
            {excludeSourcePlatforms.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                No exclusions — the AI reads every message source, including Airbnb group threads.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* CP-8406: whose replies the nightly training learns from. */}
          <AILearningSourceSetting
            scope={learnScope}
            users={learnUsers}
            disabled={savingSettings || !hasAIwritePerm}
            onScopeChange={onLearnScopeChange}
            onUsersChange={onLearnUsersChange}
          />
          </Box>
        </Paper>

        {rebookingConfig != null && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
            <MissedTourRebookingSetting
              value={rebookingConfig}
              disabled={savingSettings || !hasAIwritePerm}
              onSave={async (next) => {
                if (await saveTourOperatorConfig({ ai_missed_tour_rebooking: next })) {
                  setRebookingConfig(next);
                  enqueueSnackbar('Missed-tour rebooking settings saved', { variant: 'success' });
                }
              }}
            />
          </Paper>
        )}

        <AutoReplyRulesTable onRulesCountChange={setAutoReplyRulesCount} licenseeId={effectiveTopId} channelScope="chat" />

        <KnowledgeBaseManager
          channel="support"
          effectiveTopId={effectiveTopId}
          hasWrite={hasAIwritePerm}
          hasDelete={hasAIdeletePerm}
          webWidgetAvailable={webWidgetAvailable}
          onDocCountChange={setSupportDocCount}
          onOpenTrainingInbox={canSeeReview ? () => { setTabIndex(3); pushTabToUrl(3); window.scrollTo(0, 0); } : undefined}
        />

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>AI Response Customization</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Customize the chatbot prompt and optionally add an AI disclosure message shown to customers.
            {/* The rest of AI Suite saves on change; say so, so nobody assumes this card did too. */}
            {hasAIwritePerm && ' Changes in this section are saved when you click Save. Other settings on this page save automatically.'}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Chatbot Prompt
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              The customizable system prompt used by the chatbot when generating replies.
            </Typography>
            <TextField
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              value={chatbotPrompt}
              onChange={(e) => setChatbotPrompt(e.target.value)}
              disabled={savingPromptSettings || !hasAIwritePerm}
              placeholder="Enter your chatbot prompt..."
              variant="outlined"
              size="small"
              sx={{ maxWidth: 1200 }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
              AI Disclosure Message
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              An optional message informing customers they are communicating with an AI assistant.
            </Typography>
            <TextField
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              value={chatbotAiDisclosure}
              onChange={(e) => setChatbotAiDisclosure(e.target.value)}
              disabled={savingPromptSettings || !hasAIwritePerm}
              placeholder="e.g. This response was generated by an AI assistant..."
              variant="outlined"
              size="small"
              sx={{ maxWidth: 1200 }}
            />
          </Box>

          <Box sx={{ mb: 2, maxWidth: 1200 }}>
            {chatbotAiDisclosureTranslations.map((row, idx) => {
              const usedLangs = new Set(
                chatbotAiDisclosureTranslations.map((r, i) => (i === idx ? null : r.lang)).filter(Boolean)
              );
              const langOptions = LANGUAGES.filter((l) => !usedLangs.has(l.code));
              const selectedLangOption = LANGUAGES.find((l) => l.code === row.lang) || null;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: 1,
                    mb: 1,
                    alignItems: 'flex-start',
                  }}
                >
                  <Autocomplete
                    size="small"
                    options={langOptions}
                    getOptionLabel={(o) => `${o.name} (${o.code})`}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    value={selectedLangOption}
                    onChange={(_, v) => {
                      const next = [...chatbotAiDisclosureTranslations];
                      next[idx] = { ...next[idx], lang: v ? v.code : '' };
                      setChatbotAiDisclosureTranslations(next);
                    }}
                    disabled={savingPromptSettings || !hasAIwritePerm}
                    sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}
                    renderInput={(params) => <TextField {...params} label="Language" />}
                  />
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    value={row.text}
                    onChange={(e) => {
                      const next = [...chatbotAiDisclosureTranslations];
                      next[idx] = { ...next[idx], text: e.target.value };
                      setChatbotAiDisclosureTranslations(next);
                    }}
                    disabled={!row.lang || savingPromptSettings || !hasAIwritePerm}
                    placeholder={row.lang ? 'Translated disclosure text' : 'Pick a language first'}
                    variant="outlined"
                    size="small"
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  />
                  <DeleteIconButton
                    title="Remove translation"
                    allowed={hasAIwritePerm}
                    onClick={() => {
                      const next = chatbotAiDisclosureTranslations.filter((_, i) => i !== idx);
                      setChatbotAiDisclosureTranslations(next);
                    }}
                    disabled={savingPromptSettings}
                  />
                </Box>
              );
            })}
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Add a translation for each language you want to support. If a guest writes in a
              language without a translation, one will be auto-generated and added here for you to review and edit.
            </Typography>
            {hasAIwritePerm && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  setChatbotAiDisclosureTranslations([
                    ...chatbotAiDisclosureTranslations,
                    { lang: '', text: '' },
                  ])
                }
                disabled={savingPromptSettings || !chatbotAiDisclosure}
              >
                <Add fontSize="small" />&nbsp;Add Disclosure Translation
              </Button>
            )}
          </Box>

          {hasAIwritePerm && (
            <FormActionButtons
              mode="edit"
              size="medium"
              onSubmit={savePromptSettings}
              submitDisabled={savingPromptSettings || !promptSettingsChanged}
              leadingActions={promptSettingsChanged && !savingPromptSettings ? (
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>Unsaved changes</Typography>
              ) : null}
              submitIcon={savingPromptSettings ? <CircularProgress size={16} /> : <Save />}
              submitLabel={savingPromptSettings ? 'Saving...' : 'Save'}
            />
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          {tabsHeader}
          <Divider />
          <Box sx={{ p: 2 }}>
            <AutoTaggingRulesTab licenseeId={effectiveTopId} />
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          {tabsHeader}
          <Divider />
          <Box sx={{ p: 2 }}>
            {canSeeMetrics ? <AIMetricsTab licenseeId={effectiveTopId} /> : null}
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tabIndex} index={3}>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          {tabsHeader}
          <Divider />
          <Box sx={{ p: 2 }}>
            {canSeeReview ? (
              <KnowledgeReviewTab
                key={effectiveTopId}
                channel="support"
                licenseeId={effectiveTopId}
                autoReplyOn={autoReplyEnabled}
                onOpenCountChange={setReviewCount}
              />
            ) : null}
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tabIndex} index={4}>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          {tabsHeader}
          <Divider />
          <Box sx={{ p: 2 }}>
            {canSeeReminderGaps ? <ReminderGapsTab key={effectiveTopId} licenseeId={effectiveTopId} live={reminderGapsLive} onOpenCountChange={setReminderGapsCount} /> : null}
          </Box>
        </Paper>
      </TabPanel></>}
    </Box>
    </FormLicenseeContext.Provider>
  ) : (
    <Box p={2}>
      <PageMeta title="AI Suite" />
      <Typography variant="h6">Access Denied</Typography>
      <Typography>You do not have permission to view this page.</Typography>
    </Box>
  );
}
